import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { allCategories } from "@/lib/articles";
import { applyPageSeo } from "@/lib/seo";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db73tiun6nb9f68e9920";

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    applyPageSeo(
      "联系我们 - 鲁啊鲁",
      "欢迎联系鲁啊鲁，无论是内容合作、投稿建议还是想法交流，都可以通过联系表单或邮箱与我们取得联系。",
      "鲁啊鲁,联系我们,合作邮箱,投稿建议,联系方式"
    );
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("website_alt") as string | null)?.trim() || "";
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg && !isSpam
            ? serverMsg
            : "提交失败，请稍后重试，或直接通过邮箱与我们联系。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
        </div>

        <section className="w-full px-4 md:px-8 mt-5">
          <div className="rounded-lg bg-background-100 p-6 md:p-10">
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              联系我们
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              想说的话，欢迎告诉我们
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-foreground-700 md:text-base">
              无论是内容合作、投稿建议，还是对平台的想法与反馈，我们都很期待听到你的声音。
            </p>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 mt-10 pb-4">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
                <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  在线留言
                </h2>
                <p className="mt-1 text-sm text-foreground-600">
                  填写下面的表单，我们会尽快回复你。
                </p>

                {status === "success" ? (
                  <div className="mt-6 flex flex-col items-center rounded-lg bg-background-100 px-4 py-12 text-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-checkbox-circle-line text-4xl"></i>
                    </span>
                    <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">
                      感谢你的留言！
                    </h3>
                    <p className="mt-2 max-w-md text-sm text-foreground-600">
                      我们已经收到你的信息，会尽快与你联系。也欢迎继续浏览鲁啊鲁的其他内容。
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => setStatus("idle")}
                        className="rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        再写一条
                      </button>
                      <Link
                        to="/"
                        className="rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        返回首页
                      </Link>
                    </div>
                  </div>
                ) : (
                  <form
                    id="contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="mt-6 space-y-5"
                  >
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="contact-name"
                          className="mb-1.5 block text-sm font-medium text-foreground-800"
                        >
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          placeholder="请输入你的姓名"
                          className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="contact-email"
                          className="mb-1.5 block text-sm font-medium text-foreground-800"
                        >
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          placeholder="请输入你的邮箱"
                          className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="contact-topic"
                        className="mb-1.5 block text-sm font-medium text-foreground-800"
                      >
                        留言主题
                      </label>
                      <select
                        id="contact-topic"
                        name="topic"
                        defaultValue="内容合作"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 cursor-pointer"
                      >
                        <option value="内容合作">内容合作</option>
                        <option value="投稿建议">投稿建议</option>
                        <option value="产品反馈">产品反馈</option>
                        <option value="其他">其他</option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="contact-message"
                        className="mb-1.5 block text-sm font-medium text-foreground-800"
                      >
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={6}
                        placeholder="请输入你想说的话（最多 500 字）"
                        className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
                      ></textarea>
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      className="form-honeypot"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    {status === "error" && (
                      <p className="flex items-center gap-2 rounded-md bg-primary-100 px-4 py-2.5 text-sm text-primary-800">
                        <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                        {formError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer whitespace-nowrap"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                          提交中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
                          提交留言
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>

            <aside className="space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  联系方式
                </h2>
                <ul className="mt-4 space-y-4 text-sm text-foreground-700">
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">邮箱</span>
                      hello@lualu.com
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className="ri-phone-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">电话</span>
                      400-888-2026
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">地址</span>
                      山东省济南市历下区文化东路 88 号
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className="ri-time-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">工作时间</span>
                      周一至周五 9:00 - 18:00
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  网站地图
                </h2>
                <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
                  <li>
                    <Link to="/" className="text-foreground-700 hover:text-primary-600 transition-colors">
                      首页
                    </Link>
                  </li>
                  {allCategories.map((category) => (
                    <li key={category.id}>
                      <Link
                        to={`/category/${category.id}`}
                        className="text-foreground-700 hover:text-primary-600 transition-colors"
                      >
                        {category.name}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <Link to="/search" className="text-foreground-700 hover:text-primary-600 transition-colors">
                      搜索
                    </Link>
                  </li>
                  <li>
                    <Link to="/about" className="text-foreground-700 hover:text-primary-600 transition-colors">
                      关于我们
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" className="text-foreground-700 hover:text-primary-600 transition-colors">
                      联系我们
                    </Link>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}